"use client";

import { useState } from "react";
import FurnitureOptionalDimensionsFields, {
  SpecOptionPills,
} from "@/components/listing/FurnitureOptionalDimensionsFields";
import {
  DINING_TABLE_TOP_MATERIALS,
  formatDiningTableTopMaterialLabel,
  type DiningTableTopMaterial,
} from "@/lib/listing/diningTableChairListingSpec";
import {
  formatFurnitureMaterialLabel,
  getFurnitureSurfaceUiProfile,
  type FurnitureMaterial,
  type FurnitureSurfaceSpecFormState,
  type FurnitureSurfaceSubcategoryId,
} from "@/lib/listing/furnitureSurfaceListingSpec";
import { cn } from "@/lib/utils";

interface FurnitureSurfaceSpecFieldsProps {
  subcategoryId: FurnitureSurfaceSubcategoryId;
  value: FurnitureSurfaceSpecFormState;
  onChange: (next: FurnitureSurfaceSpecFormState) => void;
  className?: string;
}

function MaterialAndDimensions({
  subcategoryId,
  value,
  onChange,
  nestDimensions = true,
}: {
  subcategoryId: FurnitureSurfaceSubcategoryId;
  value: FurnitureSurfaceSpecFormState;
  onChange: (next: FurnitureSurfaceSpecFormState) => void;
  /** When false, show L/W/H inputs always (office-table optional panel). */
  nestDimensions?: boolean;
}) {
  const profile = getFurnitureSurfaceUiProfile(subcategoryId);
  const materialRequired =
    profile.materialKind === "table_top" ||
    profile.materialKind === "furniture";

  return (
    <div className="space-y-6">
      {profile.materialKind === "table_top" ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {profile.materialLabel}{" "}
            {materialRequired ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-text-light text-xs">(optional)</span>
            )}
          </p>
          <SpecOptionPills
            options={DINING_TABLE_TOP_MATERIALS}
            selected={value.tableTopMaterial}
            onSelect={(material: DiningTableTopMaterial) =>
              onChange({ ...value, tableTopMaterial: material })
            }
            formatLabel={formatDiningTableTopMaterialLabel}
          />
        </div>
      ) : null}

      {profile.materialKind === "furniture" ||
      profile.materialKind === "furniture_optional" ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-foreground">
            {profile.materialLabel}{" "}
            {materialRequired ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-text-light text-xs">(optional)</span>
            )}
          </p>
          <SpecOptionPills
            options={profile.furnitureMaterialOptions}
            selected={value.furnitureMaterial}
            onSelect={(material: FurnitureMaterial) =>
              onChange({ ...value, furnitureMaterial: material })
            }
            formatLabel={formatFurnitureMaterialLabel}
          />
        </div>
      ) : null}

      <FurnitureOptionalDimensionsFields
        summaryLabel={profile.dimensionsSummaryLabel}
        axes={profile.axes}
        alwaysExpanded={!nestDimensions}
        value={{
          lengthFt: value.lengthFt,
          widthFt: value.widthFt,
          heightFt: value.heightFt,
        }}
        onChange={(dims) =>
          onChange({
            ...value,
            lengthFt: dims.lengthFt,
            widthFt: dims.widthFt,
            heightFt: dims.heightFt,
          })
        }
      />
    </div>
  );
}

/**
 * Config-driven Step 2 panel for furniture surface subcategories
 * (`table`, `center-table`, `tv-console`, `wardrobe`, `dresser`, `office-table`).
 */
export default function FurnitureSurfaceSpecFields({
  subcategoryId,
  value,
  onChange,
  className = "",
}: FurnitureSurfaceSpecFieldsProps) {
  const profile = getFurnitureSurfaceUiProfile(subcategoryId);
  const hasAnyValue =
    value.tableTopMaterial !== "" ||
    value.furnitureMaterial !== "" ||
    value.lengthFt !== "" ||
    value.widthFt !== "" ||
    value.heightFt !== "";
  const [optionalExpanded, setOptionalExpanded] = useState(hasAnyValue);

  if (profile.entirelyOptionalPanel) {
    return (
      <div className={cn("space-y-2", className)}>
        <button
          type="button"
          onClick={() => setOptionalExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-3 py-2 text-left text-sm font-medium text-foreground hover:border-border-strong"
          aria-expanded={optionalExpanded}
        >
          <span>Add desk details (optional)</span>
          <span className="text-xs font-normal text-text-light">
            {optionalExpanded ? "Hide" : "Add"}
          </span>
        </button>
        {optionalExpanded ? (
          <div className="rounded-lg border border-border bg-secondary/40 p-3">
            <MaterialAndDimensions
              subcategoryId={subcategoryId}
              value={value}
              onChange={onChange}
              nestDimensions={false}
            />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      <MaterialAndDimensions
        subcategoryId={subcategoryId}
        value={value}
        onChange={onChange}
      />
    </div>
  );
}
